import React from "react";

import type { CustomerConfig, Environment, FlowStep } from "../types";

import TriggerInspector from "./inspectors/TriggerInspector";
import CustomerInspector from "./inspectors/CustomerInspector";
import BookingInspector from "./inspectors/BookingInspector";
import OutputInspector from "./inspectors/OutputInspector";
import GenericInspector from "./inspectors/GenericInspector";

type TriggerType = "email" | "recurrence" | "http";

type PayloadVariable = {
  key: string;
  label?: string;
  value: string;
  source: string;
};

type Props = {
  selectedStep?: FlowStep | null;

  triggerType: TriggerType;
  setTriggerType: React.Dispatch<React.SetStateAction<TriggerType>>;
  removeSelectedStep: () => void;
  openPayloadEditor: () => void;

  customersLoading: boolean;
  customersError?: string | null;
  selectedCustomer?: CustomerConfig | null;
  environment: Environment;
  setEnvironment: React.Dispatch<React.SetStateAction<Environment>>;
  customerSearch: string;
  setCustomerSearch: (value: string) => void;
  customerPickerOpen: boolean;
  setCustomerPickerOpen: (value: boolean) => void;
  filteredCustomers: CustomerConfig[];
  selectCustomer: (customer: CustomerConfig) => void;

  payloadVariables: PayloadVariable[];
  payloadArrays: PayloadVariable[];

  payloadMappingEnabled: boolean;
  setPayloadMappingEnabled: (value: boolean) => void;

  selectedTargetPayload: {
    label: string;
    description: string;
  };

  missingRequiredCount: number;
  openMapper: () => void;
};

function isTriggerStep(step?: FlowStep | null) {
  return Boolean(step?.actionType?.startsWith("trigger."));
}

function getActionKind(step?: FlowStep | null) {
  if (!step) return "No selection";
  if (step.actionType.startsWith("trigger.")) return "Trigger";
  if (step.actionType.startsWith("jdx.")) return "JDX action";
  if (step.actionType.startsWith("logic.")) return "Logic";
  if (step.actionType.startsWith("data.")) return "Data";
  if (step.actionType.startsWith("output.")) return "Output";
  if (step.actionType.startsWith("notification.")) return "Notification";
  return "Custom";
}

function InspectorShell({
  selectedStep,
  onRemove,
  children,
}: {
  selectedStep?: FlowStep | null;
  onRemove?: () => void;
  children: React.ReactNode;
}) {
  const canRemove = Boolean(selectedStep && !isTriggerStep(selectedStep));

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-[#080d18]">
      <div className="border-b border-slate-200 bg-slate-50/80 px-5 py-4 dark:border-white/10 dark:bg-white/[0.03]">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
              {getActionKind(selectedStep)}
            </div>

            <div className="mt-1 truncate text-base font-black tracking-tight text-slate-950 dark:text-white">
              {selectedStep?.title || "Selected action"}
            </div>

            <div className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              {selectedStep?.subtitle || "Configure the selected workflow action."}
            </div>
          </div>

          {canRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="shrink-0 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] font-black text-red-700 transition hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
            >
              Verwijder
            </button>
          )}
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-white/[0.03]">
            <div className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">
              Action
            </div>
            <div className="mt-1 truncate font-mono text-[11px] font-bold text-slate-700 dark:text-slate-300">
              {selectedStep?.actionType || "-"}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-white/[0.03]">
            <div className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">
              Source
            </div>
            <div className="mt-1 truncate font-mono text-[11px] font-bold text-slate-700 dark:text-slate-300">
              {selectedStep?.source || "default"}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-white/[0.03]">
            <div className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">
              Lock
            </div>
            <div className="mt-1 truncate font-mono text-[11px] font-bold text-slate-700 dark:text-slate-300">
              {isTriggerStep(selectedStep) ? "locked" : "editable"}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 p-4">{children}</div>
    </section>
  );
}

function renderInspector({
  selectedStep,
  triggerType,
  setTriggerType,
  openPayloadEditor,
  customersLoading,
  customersError,
  selectedCustomer,
  environment,
  setEnvironment,
  filteredCustomers,
  selectCustomer,
  missingRequiredCount,
  openMapper,
}: Props) {
  if (!selectedStep) {
    return <GenericInspector />;
  }

  if (isTriggerStep(selectedStep)) {
    return (
      <TriggerInspector
        triggerType={triggerType}
        setTriggerType={setTriggerType}
        openPayloadEditor={openPayloadEditor}
      />
    );
  }

  if (selectedStep.actionType === "jdx.select-customer") {
    return (
      <CustomerInspector
        customersLoading={customersLoading}
        customersError={customersError}
        selectedCustomer={selectedCustomer}
        environment={environment}
        setEnvironment={setEnvironment}
        filteredCustomers={filteredCustomers}
        selectCustomer={selectCustomer}
      />
    );
  }

  if (selectedStep.actionType === "jdx.create-shipment-booking") {
    return (
      <BookingInspector
        openMapper={openMapper}
        missingRequiredCount={missingRequiredCount}
      />
    );
  }

  if (selectedStep.actionType.startsWith("output.")) {
    return <OutputInspector />;
  }

  return <GenericInspector />;
}

export default function FlowInspectorPanel(props: Props) {
  const { selectedStep, removeSelectedStep } = props;

  return (
    <InspectorShell selectedStep={selectedStep} onRemove={removeSelectedStep}>
      {renderInspector(props)}
    </InspectorShell>
  );
}